import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell, { TableCellProps } from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import { graphql, StaticQuery } from 'gatsby'

interface StatsResult {
  hon: {
    elastic75: {
      search: {
        aggregations: {
          players: {
            map: {
              buckets: {
                key: string
                doc_count: number
                assists: {
                  value: number
                }
                deaths: {
                  value: number
                }
                kills: {
                  value: number
                }
                win_percent: {
                  value: number
                }
              }[]
            }
          }
        }
      }
    }
  }
}

interface PlayerStats {
  playerName: string
  winPercent: number
  kills: number
  deaths: number
  assists: number
  gameCount: number
}
type Order = 'asc' | 'desc'

const useStyles = makeStyles({
  table: {
    minWidth: 650
  }
})

interface SortTableCellProps {
  order?: Order
  onSortClick: () => void
}

const SortTableCell: React.FC<SortTableCellProps & TableCellProps> = props => {
  const { order, onSortClick, children, ...tableCellProps } = props
  return (
    <TableCell {...tableCellProps}>
      <TableSortLabel
        direction={order}
        active={order ? true : false}
        onClick={() => {
          onSortClick()
        }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  )
}

interface Props {
  playerData: PlayerStats[]
}

export const PlayerTable: React.FC<Props> = props => {
  const classes = useStyles()
  const [order, setOrder] = React.useState<Order>('desc')
  const [orderBy, setOrderBy] = React.useState<keyof PlayerStats>('winPercent')

  const handleRequestSort = (property: keyof PlayerStats) => {
    const isDesc = orderBy === property && order === 'desc'
    setOrder(isDesc ? 'asc' : 'desc')
    setOrderBy(property)
  }

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            <SortTableCell
              order={orderBy === 'playerName' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('playerName')
              }}
            >
              Player name
            </SortTableCell>
            <SortTableCell
              order={orderBy === 'winPercent' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('winPercent')
              }}
              align="right"
            >
              Win %
            </SortTableCell>
            <SortTableCell
              order={orderBy === 'kills' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('kills')
              }}
              align="right"
            >
              Kills
            </SortTableCell>
            <SortTableCell
              order={orderBy === 'deaths' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('deaths')
              }}
              align="right"
            >
              Deaths
            </SortTableCell>
            <SortTableCell
              order={orderBy === 'assists' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('assists')
              }}
              align="right"
            >
              Assists
            </SortTableCell>
            <SortTableCell
              order={orderBy === 'gameCount' ? order : undefined}
              onSortClick={() => {
                handleRequestSort('gameCount')
              }}
              align="right"
            >
              Games played
            </SortTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.playerData
            .sort((a, b) => ((order === 'asc' ? a[orderBy] > b[orderBy] : a[orderBy] < b[orderBy]) ? 1 : -1))
            .map(row => (
              <TableRow key={row.playerName}>
                <TableCell component="th" scope="row">
                  {row.playerName}
                </TableCell>
                <TableCell align="right">{row.winPercent.toFixed(2)}</TableCell>
                <TableCell align="right">{row.kills.toFixed(2)}</TableCell>
                <TableCell align="right">{row.deaths.toFixed(2)}</TableCell>
                <TableCell align="right">{row.assists.toFixed(2)}</TableCell>
                <TableCell align="right">{row.gameCount}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function transformToPlayerData(result: StatsResult) {
  return result.hon.elastic75.search.aggregations.players.map.buckets
    .filter(b => (b.key ? true : false))
    .map(b => {
      return {
        playerName: b.key,
        winPercent: b.win_percent.value * 100,
        kills: b.kills.value,
        deaths: b.deaths.value,
        assists: b.assists.value,
        gameCount: b.doc_count
      }
    })
}

export const PlayerTableWithData = () => (
  <StaticQuery
    query={graphql`
      query PlayerStatsResult {
        hon {
          elastic75 {
            search(index: "matches_new", size: 0, body: "{ \"size\": 0, \"query\": { \"bool\": { \"must\": [ { \"match\": { \"matchSummary.map\": \"midwars\" } }, { \"match\": { \"matchSummary.officl\": \"1\" } }, { \"match\": { \"matchSummary.version\": \"4.9.1.3\" } } ] } }, \"aggs\": { \"players\": { \"nested\": { \"path\": \"players\" }, \"aggs\": { \"map\": { \"terms\": { \"field\": \"players.nickname\", \"min_doc_count\": 20, \"size\": 2000000 }, \"aggs\": { \"win_percent\": { \"avg\": { \"field\": \"players.wins\" } }, \"kills\": { \"avg\": { \"field\": \"players.herokills\" } }, \"deaths\": { \"avg\": { \"field\": \"players.deaths\" } }, \"assists\": { \"avg\": { \"field\": \"players.heroassists\" } }, \"win_sort\": { \"bucket_sort\": { \"sort\": [{ \"win_percent\": { \"order\": \"desc\" } }], \"size\": 200 } } } } } } }}")
          }
        }
      }
    `}
    render={(data: StatsResult) => <PlayerTable playerData={transformToPlayerData(data)} />}
  />
)